import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/catalyst/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useProviderDirectoryV2ContactPointsCreateMutation } from '@/services/api/generated/providerDirectoryV2ContactPointsService';
import {
  useCreateProviderDirectoryLocationMutation,
} from '@/services/api/providerDirectoryV2Service';
import type { ProviderDirectoryV2Service } from '@/services/types/provider-directory-v2-types';

type Slot = 'clinical' | 'billing' | 'records';

type ContactDraft = {
  point_type: 'PHONE' | 'FAX' | 'EMAIL';
  value: string;
};

const slotTitle = (slot: Slot) => {
  if (slot === 'clinical') return 'Clinical location';
  if (slot === 'billing') return 'Billing department';
  return 'Records department';
};

const slotPurpose = (slot: Slot): 'GENERAL' | 'BILLING' | 'RECORDS' => {
  if (slot === 'billing') return 'BILLING';
  if (slot === 'records') return 'RECORDS';
  return 'GENERAL';
};

const defaultLabel = (slot: Slot, service: ProviderDirectoryV2Service) => {
  let role = 'Clinical';
  if (slot === 'billing') role = 'Billing';
  else if (slot === 'records') role = 'Records';
  const city = service.clinical_location?.address?.city ?? null;
  const cityPart = city ? ` ${city}` : '';
  return `${service.name}${cityPart} ${role}`.trim();
};

export const CreateDepartmentModal = ({
  open,
  onClose,
  slot,
  service,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  slot: Slot;
  service: ProviderDirectoryV2Service;
  onCreated: (locationId: string) => void;
}) => {
  const [label, setLabel] = useState('');
  const [contacts, setContacts] = useState<ContactDraft[]>([
    { point_type: 'FAX', value: '' },
  ]);
  const [addAddress, setAddAddress] = useState(false);
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');

  const [createLocation, { isLoading: isCreatingLocation }] =
    useCreateProviderDirectoryLocationMutation();
  const [createContactPoint, { isLoading: isCreatingContact }] =
    useProviderDirectoryV2ContactPointsCreateMutation();

  const isSaving = isCreatingLocation || isCreatingContact;

  useEffect(() => {
    if (!open) return;
    setLabel(defaultLabel(slot, service));
    setContacts([{ point_type: 'FAX', value: '' }]);
    setAddAddress(false);
    setAddressLine1('');
    setAddressLine2('');
    setCity(service.clinical_location?.address?.city ?? '');
    setState(service.clinical_location?.address?.state ?? '');
    setPostalCode(service.clinical_location?.address?.postal_code ?? '');
  }, [open, service, slot]);

  const canSave = useMemo(() => {
    const hasAnyContact = contacts.some((c) => c.value.trim().length > 0);
    return Boolean(label.trim()) && hasAnyContact;
  }, [contacts, label]);

  const handleSave = async () => {
    if (!canSave) return;
    try {
      const location = await createLocation({
        name: label.trim(),
        location_type: 'OTHER',
        status: 'ACTIVE',
        notes: null,
        organization_id: null,
      }).unwrap();

      const purpose = slotPurpose(slot);
      const contactCreates = contacts
        .map((c) => ({ ...c, value: c.value.trim() }))
        .filter((c) => c.value.length > 0)
        .map((c) =>
          createContactPoint({
            providerDirectoryV2ContactPointCreateRequest: {
              owner_type: 'location',
              owner_id: location.id,
              point_type: c.point_type,
              purpose,
              value: c.value,
              is_preferred: false,
              source_mode: 'MANUAL',
              source_type: 'CALLED_PROVIDER',
            },
          }).unwrap(),
        );

      if (
        addAddress &&
        (addressLine1.trim() ||
          city.trim() ||
          state.trim() ||
          postalCode.trim())
      ) {
        contactCreates.push(
          createContactPoint({
            providerDirectoryV2ContactPointCreateRequest: {
              owner_type: 'location',
              owner_id: location.id,
              point_type: 'MAILING_ADDRESS',
              purpose: 'GENERAL',
              is_preferred: true,
              address_line1: addressLine1.trim() || null,
              address_line2: addressLine2.trim() || null,
              city: city.trim() || null,
              state: state.trim() || null,
              postal_code: postalCode.trim() || null,
              country: 'US',
              source_mode: 'MANUAL',
              source_type: 'CALLED_PROVIDER',
            },
          }).unwrap(),
        );
      }

      await Promise.all(contactCreates);

      toast.success(`${slotTitle(slot)} created`);
      onCreated(location.id);
      onClose();
    } catch (err) {
      toast.error('Failed to create. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create {slotTitle(slot)}</DialogTitle>
          <DialogDescription>
            Contact-only is allowed. Add an address if you have one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <div className="text-xs font-medium text-zinc-700">Label</div>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs font-medium text-zinc-700">
                Contact methods (at least one)
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setContacts((prev) => [
                    ...prev,
                    { point_type: 'FAX', value: '' },
                  ])
                }
              >
                <Plus className="h-4 w-4 mr-1" />
                Add contact
              </Button>
            </div>

            <div className="space-y-2">
              {contacts.map((c, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    className="h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                    value={c.point_type}
                    onChange={(e) => {
                      const next = e.target.value as ContactDraft['point_type'];
                      setContacts((prev) =>
                        prev.map((p, i) =>
                          i === idx ? { ...p, point_type: next } : p,
                        ),
                      );
                    }}
                  >
                    <option value="FAX">Fax</option>
                    <option value="PHONE">Phone</option>
                    <option value="EMAIL">Email</option>
                  </select>
                  <Input
                    value={c.value}
                    onChange={(e) => {
                      const next = e.target.value;
                      setContacts((prev) =>
                        prev.map((p, i) =>
                          i === idx ? { ...p, value: next } : p,
                        ),
                      );
                    }}
                    placeholder="Enter value…"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setContacts((prev) => prev.filter((_, i) => i !== idx))
                    }
                    disabled={contacts.length === 1}
                  >
                    <Trash2 className="h-4 w-4 text-zinc-500" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="provider-directory-create-department-add-address"
              className="flex items-center gap-2 text-sm text-zinc-700"
            >
              <input
                id="provider-directory-create-department-add-address"
                type="checkbox"
                checked={addAddress}
                onChange={(e) => setAddAddress(e.target.checked)}
              />
              <span>Add mailing address (optional)</span>
            </label>

            {addAddress ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="sm:col-span-2">
                  <Input
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    placeholder="Address line 1"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Input
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    placeholder="Address line 2"
                  />
                </div>
                <Input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                />
                <Input
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="State"
                />
                <Input
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="ZIP"
                />
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave || isSaving}>
              Create
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
